import React, { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { CapacityTable } from "~/components/Capacity/CapacityTable";
import { UtilizationTable } from "~/components/Capacity/UtilizationTable";
import SectionLoader from "~/components/SectionLoader/SectionLoader";

import AppsAndIntegrationsPage from "~/pages/AppsAndIntegrations/AppsAndIntegrationsPage";
import PayPalIntegrationPage from "~/pages/AppsAndIntegrations/Intergations/PayPalIntegrationPage";
import StripeIntegrationPage from "~/pages/AppsAndIntegrations/Intergations/StripeIntegrationPage";
import WebhooksIntegrationsPage from "~/pages/AppsAndIntegrations/Intergations/WebhooksIntegrationsPage";
import GettingStartedPage from "~/pages/GettingStarted/GettingStartedPage";
import NewFeaturesPage from "~/pages/NewFeatures/NewFeaturesPage";
import ProjectTasksArchivePage from "~/pages/ProjectTasksArchive/ProjectTasksArchivePage";
import { PROJECT_TEMPLATE_NOTE } from "~/pages/ProjectTemplates/utils";
import { RequireCanAccessAppsAndIntegrations } from "~/routes/RequireCanAccessAppsAndIntegrations";
import { RequireCanManageProject } from "~/routes/RequireCanManageProject";
import { RequireCanSeeGettingStarted } from "~/routes/RequireCanSeeGettingStarted";
import { RequireCanSeeUserTimesheet } from "~/routes/RequireCanSeeUserTimesheet";
import { RequireExistingCompanyAccess } from "~/routes/RequireExistingCompanyAccess";
import { RequireFeature } from "~/routes/RequireFeature";
import { RequirePowerUser } from "~/routes/RequirePowerUser";
import { RequireQuickBooksFeature } from "~/routes/RequireQuickBooksFeature";
import { RequireSecurityFirewall } from "~/routes/RequireSecurityFirewall";
import { getConfigOption, getRawConfigOption, getUserDefaultHomepage } from "~/store/selectors/selector.configOptions";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags";
import { RequireAccess } from "./RequireAccess";
import { RequireActiveUserAccess } from "./RequireActiveUserAccess";
import { RequireAdditionalPermissions } from "./RequireAdditionalPermissions";
import { RequireExistingUserAccess } from "./RequireExistingUserAccess";

const NotFoundPage = lazy(() => import("~/pages/NotFoundPage"));
const ProjectFormPage = lazy(() => import("~/pages/ProjectFormPage"));
const ProjectsPage = lazy(() => import("~/pages/Projects/ProjectsPage"));
const ProjectInvitePeople = lazy(() => import("~/pages/Project/ProjectInvitePeople"));
const ProjectPage = lazy(() => import("~/pages/Project/ProjectPage"));
const ProjectTemplatePage = lazy(() => import("~/pages/ProjectTemplates/ProjectTemplatePage.jsx"));
const TemplateTaskPage = lazy(() => import("~/pages/ProjectTemplates/TemplateTaskPage.jsx"));
const TemplateDiscussionAndNotePage = lazy(() =>
  import("~/pages/ProjectTemplates/DiscussionsAndNotes/TemplateDiscussionAndNotePage.jsx")
);
const ActivityPage = lazy(() => import("~/pages/ActivityPage"));
const TimesheetPage = lazy(() => import("~/pages/TimesheetPage"));
const MyWorkPage = lazy(() => import("~/pages/MyWorkPage"));
const MyUpdatesPage = lazy(() => import("~/pages/MyUpdatesPage"));
const WorkloadPage = lazy(() => import("~/pages/WorkloadPage"));
const WhiteboardsPage = lazy(() => import("~/pages/WhiteboardsPage"));
const WhiteboardPageContainer = lazy(() => import("~/components/Whiteboards/WhiteboardPageContainer"));
const TaskPage = lazy(() => import("~/pages/TaskPage"));
const DiscussionPage = lazy(() => import("~/pages/DiscussionPage"));
const NotePage = lazy(() => import("~/pages/NotePage"));
const PersonalSettingsPage = lazy(() => import("~/pages/PersonalSettingsPage"));
const SystemSettingsPage = lazy(() => import("~/pages/SystemSettingsPage"));
const GeneralSettingsPage = lazy(() => import("~/pages/SystemSettings/GeneralSettingsPage"));
const WorkdaySettingsPage = lazy(() => import("~/pages/SystemSettings/WorkdaySettingsPage"));
const ProjectSettingsPage = lazy(() => import("~/pages/SystemSettings/ProjectSettingsPage"));
const CalendarSettingsPage = lazy(() => import("~/pages/SystemSettings/CalendarSettingsPage"));
const TrackingSettingsPage = lazy(() => import("~/pages/SystemSettings/TrackingSettingsPage"));
const InvoiceSettingsPage = lazy(() => import("~/pages/SystemSettings/InvoiceSettingsPage"));
const InvoiceDesignerPage = lazy(() => import("~/pages/SystemSettings/InvoiceDesignerPage"));
const SecuritySettingsPage = lazy(() => import("~/pages/SystemSettings/SecuritySettingsPage"));
const ProjectActivityPage = React.lazy(() => import("~/pages/Project/Sections/ProjectActivityPage"));
const ProjectDiscussionsPage = React.lazy(() => import("~/pages/Project/Sections/ProjectDiscussionsPage"));
const ProjectTasksPage = React.lazy(() => import("~/pages/Project/Sections/ProjectTasksPage"));
const ProjectFoldersPage = React.lazy(() => import("~/pages/Project/Sections/ProjectFoldersPage"));
const OtherProjectsPage = React.lazy(() => import("~/pages/Projects/Sections/OtherProjectsPage"));
const CompletedProjectsPage = React.lazy(() => import("~/pages/Projects/Sections/CompletedProjectsPage"));

const SelfHostedSettingsPage = React.lazy(() => import("~/pages/SelfHostedSettingsPage"));
const ZapierPage = React.lazy(() => import("~/pages/AppsAndIntegrations/Intergations/ZapierPage"));
const GoogleDrivePage = React.lazy(() => import("~/pages/AppsAndIntegrations/Intergations/GoogleDrivePage"));
const DropboxPage = React.lazy(() => import("~/pages/AppsAndIntegrations/Intergations/DropboxPage"));
const QuickBooksPage = React.lazy(() => import("~/pages/AppsAndIntegrations/Intergations/QuickBooksIntegrationPage"));
const SlackIntegrationPage = React.lazy(() => import("~/pages/AppsAndIntegrations/Intergations/SlackIntegrationPage"));

const EmailLogPage = React.lazy(() => import("~/pages/SelfHostedSettings/EmailLogPage"));

const InvoicesPage = React.lazy(() => import("~/pages/Invoices/InvoicesPage"));
const ViewInvoicePage = React.lazy(() => import("~/pages/Invoices/ViewInvoicePage"));
const AddInvoicePage = React.lazy(() => import("~/pages/Invoices/AddInvoicePage"));
const AddInvoiceFromEstimatePage = React.lazy(() => import("~/pages/Invoices/AddInvoiceFromEstimatePage"));
const EditInvoicePage = React.lazy(() => import("~/pages/Invoices/EditInvoicePage"));
const DuplicateInvoicePage = React.lazy(() => import("~/pages/Invoices/DuplicateInvoicePage"));

const EstimatesPage = React.lazy(() => import("~/pages/Estimates/EstimatesPage"));
const ViewEstimatePage = React.lazy(() => import("~/pages/Estimates/ViewEstimatePage"));
const AddEstimatePage = React.lazy(() => import("~/pages/Estimates/AddEstimatePage"));
const EditEstimatePage = React.lazy(() => import("~/pages/Estimates/EditEstimatePage"));
const DuplicateEstimatePage = React.lazy(() => import("~/pages/Estimates/DuplicateEstimatePage"));

const ViewRecurringProfilePage = React.lazy(() => import("~/pages/RecurringProfiles/ViewRecurringProfilePage"));
const AddRecurringProfilePage = React.lazy(() => import("~/pages/RecurringProfiles/AddRecurringProfilePage"));
const EditRecurringProfilePage = React.lazy(() => import("~/pages/RecurringProfiles/EditRecurringProfilePage"));
const DuplicateRecurringProfilePage = React.lazy(() =>
  import("~/pages/RecurringProfiles/DuplicateRecurringProfilePage")
);

const ManageTaskLabelsPage = React.lazy(() => import("~/pages/Project/Sections/ManageTaskLabelsPage"));

const SearchPage = React.lazy(() => import("~/pages/Search/SearchPage"));
const ProjectNotesPage = React.lazy(() => import("~/pages/Project/Sections/ProjectNotesPage"));
const ProjectTimePage = React.lazy(() => import("~/pages/Project/Sections/ProjectTimePage"));
const ProjectExpensesPage = React.lazy(() => import("~/pages/Project/Sections/ProjectExpensesPage"));
const ReportsPage = React.lazy(() => import("~/pages/Reports/ReportsPage"));
const AssignmentsPage = React.lazy(() => import("~/pages/Reports/Outlets/AssignmentsPage"));
const TimeDashboardPage = React.lazy(() => import("~/pages/Reports/Outlets/TimeDashboardPage"));
const AllReportsPage = React.lazy(() => import("~/pages/Reports/Outlets/AllReportsPage"));
const TimeFilterPage = React.lazy(() => import("~/pages/Reports/TimeFilterPage"));
const TasksFilterPage = React.lazy(() => import("~/pages/Reports/TasksFilterPage"));
const ProjectsFilterPage = React.lazy(() => import("~/pages/Reports/ProjectsFilterPage"));
const UninvoicedReportPage = React.lazy(() => import("~/pages/Reports/Uninvoiced/UninvoicedReportPage"));
const InvoicesFilterPage = React.lazy(() => import("~/pages/Reports/InvoicesFilterPage"));
const PaymentsFilterPage = React.lazy(() => import("~/pages/Reports/PaymentsFilterPage"));

const PeoplePage = React.lazy(() => import("~/pages/People/PeoplePage"));
const PeopleCompanies = React.lazy(() => import("~/pages/People/PeopleCompanies"));
const PeopleTeams = React.lazy(() => import("~/pages/People/PeopleTeams"));
const PeopleInvites = React.lazy(() => import("~/pages/People/PeopleInvites"));
const PeopleArchive = React.lazy(() => import("~/pages/People/Archive/PeopleArchive"));
const TrashPage = React.lazy(() => import("~/pages/Trash/TrashPage"));

const UserProfilePage = React.lazy(() => import("~/pages/People/UserProfile/UserProfilePage"));
const UserProfileProjectPage = React.lazy(() => import("~/pages/People/UserProfile/UserProfileProjectPage"));
const UserProfileTasksPage = React.lazy(() => import("~/pages/People/UserProfile/UserProfileTasksPage"));
const UserProfileTimesheetPage = React.lazy(() => import("~/pages/People/UserProfile/UserProfileTimesheetPage"));
const UserProfileActivityPage = React.lazy(() => import("~/pages/People/UserProfile/UserProfileActivityPage"));
const UserProfileAvailabilityPage = React.lazy(() => import("~/pages/People/UserProfile/UserProfileAvailabilityPage"));

const CompanyProfilePage = React.lazy(() => import("~/pages/People/CompanyProfile/CompanyProfilePage"));
const CompanyProfileProjectsPage = React.lazy(() => import("~/pages/People/CompanyProfile/CompanyProfileProjectsPage"));
const CompanyProfileMembersPage = React.lazy(() => import("~/pages/People/CompanyProfile/CompanyProfileMembersPage"));
const CompanyProfileInvoicesPage = React.lazy(() => import("~/pages/People/CompanyProfile/CompanyProfileInvoicesPage"));
const CompanyProfileEstimatesPage = React.lazy(() =>
  import("~/pages/People/CompanyProfile/CompanyProfileEstimatesPage")
);

const CapacityPage = React.lazy(() => import("~/pages/CapacityPage/CapacityPage"));
const CapacityNoAvailabilityPage = React.lazy(() => import("~/pages/CapacityPage/CapacityNoAvailabilityPage"));
const CalendarPage = React.lazy(() => import("~/pages/Calendar/CalendarPage"));
const CalendarEventPage = React.lazy(() => import("~/pages/Calendar/CalendarEventPage"));
const TeamProfilePage = React.lazy(() => import("~/pages/People/Teams/TeamProfilePage"));
const TeamMembersPage = React.lazy(() => import("~/pages/People/Teams/TeamMembersPage"));
const TeamTasksPage = React.lazy(() => import("~/pages/People/Teams/TeamTasksPage"));

const MainRoutes = () => {
  const url = useSelector(getUserDefaultHomepage);

  const expenseEnabled = useSelector((state) => getConfigOption(state, "expense_tracking_enabled"));
  const availabilityEnabled = useSelector((state) => getConfigOption(state, "availability_enabled"));
  const utilizationEnabled = useSelector((state) => getRawConfigOption(state, "utilization_enabled"));
  const capacityFF = useSelector((state) => getFeatureFlag(state, "capacity"));

  return (
    <Routes>
      <Route
        index
        element={<Navigate to={`/${url}`} />}
      />
      <Route
        path="project-templates"
        element={<Navigate to="/projects/templates" />}
      />
      <Route
        path="projects/templates/:templateId/tasks/:taskId"
        element={
          <RequirePowerUser>
            <Suspense fallback={<SectionLoader />}>
              <TemplateTaskPage />
            </Suspense>
          </RequirePowerUser>
        }
      />
      <Route
        path="projects/templates/:templateId/discussions/:elementId"
        element={
          <RequirePowerUser>
            <Suspense fallback={<SectionLoader />}>
              <TemplateDiscussionAndNotePage />
            </Suspense>
          </RequirePowerUser>
        }
      />
      <Route
        path="projects/templates/:templateId/notes/:elementId"
        element={
          <RequirePowerUser>
            <Suspense fallback={<SectionLoader />}>
              <TemplateDiscussionAndNotePage type={PROJECT_TEMPLATE_NOTE} />
            </Suspense>
          </RequirePowerUser>
        }
      />
      <Route
        path="my-work"
        element={
          <RequireAccess section="my_work">
            <Suspense fallback={<SectionLoader />}>
              <MyWorkPage />
            </Suspense>
          </RequireAccess>
        }
      />
      <Route
        path="getting-started"
        element={
          <RequireCanSeeGettingStarted>
            <Suspense fallback={<SectionLoader />}>
              <GettingStartedPage />
            </Suspense>
          </RequireCanSeeGettingStarted>
        }
      />
      <Route
        path="my-updates"
        element={
          <Suspense fallback={<SectionLoader />}>
            <MyUpdatesPage />
          </Suspense>
        }
      />
      <Route
        path="activity"
        element={
          <Suspense fallback={<SectionLoader />}>
            <ActivityPage />
          </Suspense>
        }
      />
      <Route
        path="timesheet"
        element={
          <RequireAccess section="timesheet">
            <Suspense fallback={<SectionLoader />}>
              <TimesheetPage />
            </Suspense>
          </RequireAccess>
        }
      />
      <Route
        path="workload"
        element={
          <RequireAccess section="workload">
            <Suspense fallback={<SectionLoader />}>
              <WorkloadPage />
            </Suspense>
          </RequireAccess>
        }
      />
      <Route
        path="whiteboards"
        element={
          <RequireAccess section="whiteboards">
            <Suspense fallback={<SectionLoader />}>
              <WhiteboardsPage />
            </Suspense>
          </RequireAccess>
        }
      />
      <Route
        path="/whiteboards/:whiteboardId"
        element={
          <RequireAccess section="whiteboards">
            <WhiteboardPageContainer />
          </RequireAccess>
        }
      />
      <Route
        path="projects"
        element={
          <Suspense fallback={<SectionLoader />}>
            <ProjectsPage />
          </Suspense>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<SectionLoader />}>
              <OtherProjectsPage />
            </Suspense>
          }
        />
        <Route
          path="archive"
          element={
            <Suspense fallback={<SectionLoader />}>
              <CompletedProjectsPage />
            </Suspense>
          }
        />
        <Route
          path="templates"
          element={
            <RequirePowerUser>
              <Suspense fallback={<SectionLoader />}>
                <OtherProjectsPage />
              </Suspense>
            </RequirePowerUser>
          }
        />
      </Route>
      <Route
        path="projects/templates/:templateId"
        element={
          <RequirePowerUser>
            <Suspense fallback={<SectionLoader />}>
              <ProjectTemplatePage />
            </Suspense>
          </RequirePowerUser>
        }
      />
      <Route
        path="projects/add"
        element={
          <RequirePowerUser>
            <Suspense fallback={<SectionLoader />}>
              <ProjectFormPage />
            </Suspense>
          </RequirePowerUser>
        }
      />
      <Route
        path="projects/:projectId/edit"
        element={
          <RequireCanManageProject>
            <Suspense fallback={<SectionLoader />}>
              <ProjectFormPage />
            </Suspense>
          </RequireCanManageProject>
        }
      />
      <Route
        path="projects/:projectId/members"
        element={
          <RequireCanManageProject>
            <Suspense fallback={<SectionLoader />}>
              <ProjectInvitePeople />
            </Suspense>
          </RequireCanManageProject>
        }
      />
      <Route
        path="projects/:projectId"
        element={
          <Suspense fallback={<SectionLoader />}>
            <ProjectPage />
          </Suspense>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<SectionLoader />}>
              <ProjectTasksPage />
            </Suspense>
          }
        />
        <Route
          path="activity"
          element={
            <Suspense fallback={<SectionLoader />}>
              <ProjectActivityPage />
            </Suspense>
          }
        />
        {expenseEnabled && (
          <Route
            path="expenses"
            element={
              <Suspense fallback={<SectionLoader />}>
                <ProjectExpensesPage />
              </Suspense>
            }
          />
        )}
        <Route
          path="discussions"
          element={
            <Suspense fallback={<SectionLoader />}>
              <ProjectDiscussionsPage />
            </Suspense>
          }
        />
        <Route
          path="notes"
          element={
            <Suspense fallback={<SectionLoader />}>
              <ProjectNotesPage />
            </Suspense>
          }
        />
        <Route
          path="time-records"
          element={
            <Suspense fallback={<SectionLoader />}>
              <ProjectTimePage />
            </Suspense>
          }
        />
        <Route
          path="notebooks/:id"
          element={
            <Suspense fallback={<SectionLoader />}>
              <ProjectNotesPage />
            </Suspense>
          }
        />
        <Route
          path="files"
          element={<Navigate to="../folders" />}
        />
        <Route path="folders">
          <Route
            index
            element={
              <Suspense fallback={<SectionLoader />}>
                <ProjectFoldersPage />
              </Suspense>
            }
          />
          <Route
            path=":folderId"
            element={
              <Suspense fallback={<SectionLoader />}>
                <ProjectFoldersPage />
              </Suspense>
            }
          />
        </Route>
      </Route>
      <Route path="projects/:projectId/discussions/:id">
        <Route
          index
          element={
            <Suspense fallback={<SectionLoader />}>
              <DiscussionPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="projects/:projectId/notes/:id">
        <Route
          index
          element={
            <Suspense fallback={<SectionLoader />}>
              <NotePage />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="projects/:project_id/task-labels"
        element={
          <Suspense fallback={<SectionLoader />}>
            <ManageTaskLabelsPage />
          </Suspense>
        }
      />
      <Route
        path="projects/:projectId/tasks/archive"
        element={
          <Suspense fallback={<SectionLoader />}>
            <ProjectTasksArchivePage />
          </Suspense>
        }
      />
      <Route
        path="projects/:projectId/tasks/:id"
        element={
          <Suspense fallback={<SectionLoader />}>
            <TaskPage />
          </Suspense>
        }
      />
      <Route
        exact
        path="personal-settings"
        element={
          <Suspense fallback={<SectionLoader />}>
            <PersonalSettingsPage />
          </Suspense>
        }
      />
      <Route path="integrations">
        <Route
          index
          element={
            <RequireCanAccessAppsAndIntegrations>
              <Suspense fallback={<SectionLoader />}>
                <AppsAndIntegrationsPage />
              </Suspense>
            </RequireCanAccessAppsAndIntegrations>
          }
        />
        <Route
          path="quickbooks"
          element={
            <RequireCanAccessAppsAndIntegrations>
              <RequireQuickBooksFeature>
                <Suspense fallback={<SectionLoader />}>
                  <QuickBooksPage />
                </Suspense>
              </RequireQuickBooksFeature>
            </RequireCanAccessAppsAndIntegrations>
          }
        />
        <Route
          path="zapier"
          element={
            <RequireCanAccessAppsAndIntegrations>
              <Suspense fallback={<SectionLoader />}>
                <ZapierPage />
              </Suspense>
            </RequireCanAccessAppsAndIntegrations>
          }
        />
        <Route
          path="google-drive"
          element={
            <RequireAccess section="self_hosted_settings">
              <RequireCanAccessAppsAndIntegrations>
                <Suspense fallback={<SectionLoader />}>
                  <GoogleDrivePage />
                </Suspense>
              </RequireCanAccessAppsAndIntegrations>
            </RequireAccess>
          }
        />
        <Route
          path="dropbox"
          element={
            <RequireAccess section="self_hosted_settings">
              <RequireCanAccessAppsAndIntegrations>
                <Suspense fallback={<SectionLoader />}>
                  <DropboxPage />
                </Suspense>
              </RequireCanAccessAppsAndIntegrations>
            </RequireAccess>
          }
        />
        <Route
          exact
          path="webhooks"
          element={
            <RequireCanAccessAppsAndIntegrations>
              <Suspense fallback={<SectionLoader />}>
                <WebhooksIntegrationsPage />
              </Suspense>
            </RequireCanAccessAppsAndIntegrations>
          }
        />
        <Route
          exact
          path="slack"
          element={
            <RequireCanAccessAppsAndIntegrations>
              <Suspense fallback={<SectionLoader />}>
                <SlackIntegrationPage />
              </Suspense>
            </RequireCanAccessAppsAndIntegrations>
          }
        />
        <Route
          exact
          path="paypal"
          element={
            <RequireCanAccessAppsAndIntegrations>
              <Suspense fallback={<SectionLoader />}>
                <PayPalIntegrationPage />
              </Suspense>
            </RequireCanAccessAppsAndIntegrations>
          }
        />
        <Route
          exact
          path="stripe"
          element={
            <RequireCanAccessAppsAndIntegrations>
              <Suspense fallback={<SectionLoader />}>
                <StripeIntegrationPage />
              </Suspense>
            </RequireCanAccessAppsAndIntegrations>
          }
        />
      </Route>
      <Route path="system-settings">
        <Route
          index
          element={
            <RequireAccess section="system_settings">
              <Suspense fallback={<SectionLoader />}>
                <SystemSettingsPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="general"
          element={
            <RequireAccess section="system_settings">
              <Suspense fallback={<SectionLoader />}>
                <GeneralSettingsPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="workday"
          element={
            <RequireAccess section="system_settings">
              <Suspense fallback={<SectionLoader />}>
                <WorkdaySettingsPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="project"
          element={
            <RequireAccess section="system_settings">
              <Suspense fallback={<SectionLoader />}>
                <ProjectSettingsPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="calendar"
          element={
            <RequireAccess section="system_settings">
              <Suspense fallback={<SectionLoader />}>
                <CalendarSettingsPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="tracking"
          element={
            <RequireAccess section="system_settings">
              <Suspense fallback={<SectionLoader />}>
                <TrackingSettingsPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="invoice"
          element={
            <RequireAccess section="system_settings">
              <Suspense fallback={<SectionLoader />}>
                <InvoiceSettingsPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="invoice-designer"
          element={
            <RequireAccess section="system_settings">
              <Suspense fallback={<SectionLoader />}>
                <InvoiceDesignerPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="payments"
          element={
            <RequireAccess section="system_settings">
              <Suspense fallback={<SectionLoader />}>
                <NotFoundPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="security"
          element={
            <RequireAccess section="system_settings">
              <RequireSecurityFirewall>
                <Suspense fallback={<SectionLoader />}>
                  <SecuritySettingsPage />
                </Suspense>
              </RequireSecurityFirewall>
            </RequireAccess>
          }
        />
      </Route>
      <Route path="self-hosted-settings">
        <Route
          index
          element={
            <RequireAccess section="self_hosted_settings">
              <Suspense fallback={<SectionLoader />}>
                <SelfHostedSettingsPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="email-log"
          element={
            <RequireAccess section="self_hosted_settings">
              <Suspense fallback={<SectionLoader />}>
                <EmailLogPage />
              </Suspense>
            </RequireAccess>
          }
        />
      </Route>
      <Route path="calendar">
        <Route
          index
          element={
            <RequireAccess section="calendar">
              <Suspense fallback={<SectionLoader />}>
                <CalendarPage />
              </Suspense>
            </RequireAccess>
          }
        />
      </Route>
      <Route
        path="calendars/:calendarId/events/:id"
        element={
          <RequireAccess section="calendar">
            <Suspense fallback={<SectionLoader />}>
              <CalendarEventPage />
            </Suspense>
          </RequireAccess>
        }
      />
      {capacityFF ? (
        <Route
          path="capacity"
          element={
            <RequireAccess section="capacity">
              <Suspense fallback={<SectionLoader />}>
                {availabilityEnabled ? <CapacityPage /> : <CapacityNoAvailabilityPage />}
              </Suspense>
            </RequireAccess>
          }
        >
          <Route
            index
            element={
              <RequireAccess section="capacity">
                <Suspense fallback={<SectionLoader />}>
                  <CapacityTable />
                </Suspense>
              </RequireAccess>
            }
          />
          {utilizationEnabled ? (
            <Route
              path="utilization"
              element={
                <RequireAccess section="capacity">
                  <Suspense fallback={<SectionLoader />}>
                    <UtilizationTable />
                  </Suspense>
                </RequireAccess>
              }
            />
          ) : null}
        </Route>
      ) : null}
      <Route path="invoices">
        <Route
          index
          element={
            <RequireAccess section="invoices">
              <Suspense fallback={<SectionLoader />}>
                <InvoicesPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path=":id"
          element={
            <RequireAdditionalPermissions checkQuickBooksIntegrationInUse>
              <RequireAccess section="invoices">
                <Suspense fallback={<SectionLoader />}>
                  <ViewInvoicePage />
                </Suspense>
              </RequireAccess>
            </RequireAdditionalPermissions>
          }
        />
        <Route
          path=":id/edit"
          element={
            <RequireAccess section="invoices">
              <Suspense fallback={<SectionLoader />}>
                <EditInvoicePage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path=":id/duplicate"
          element={
            <RequireAccess section="invoices">
              <Suspense fallback={<SectionLoader />}>
                <DuplicateInvoicePage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="add"
          element={
            <RequireAccess section="invoices">
              <Suspense fallback={<SectionLoader />}>
                <AddInvoicePage />
              </Suspense>
            </RequireAccess>
          }
        />
      </Route>
      <Route path="estimates">
        <Route
          index
          element={
            <RequireAccess section="estimates">
              <Suspense fallback={<SectionLoader />}>
                <EstimatesPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path=":id"
          element={
            <RequireAccess section="estimates">
              <Suspense fallback={<SectionLoader />}>
                <ViewEstimatePage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path=":id/duplicate"
          element={
            <RequireAccess section="estimates">
              <Suspense fallback={<SectionLoader />}>
                <DuplicateEstimatePage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path=":id/edit"
          element={
            <RequireAccess section="estimates">
              <Suspense fallback={<SectionLoader />}>
                <EditEstimatePage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="add"
          element={
            <RequireAccess section="estimates">
              <Suspense fallback={<SectionLoader />}>
                <AddEstimatePage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path=":id/create-invoice"
          element={
            <RequireAccess section="estimates">
              <Suspense fallback={<SectionLoader />}>
                <AddInvoiceFromEstimatePage />
              </Suspense>
            </RequireAccess>
          }
        />
      </Route>

      <Route path="recurring-profiles">
        <Route
          path=":id"
          element={
            <RequireAccess section="recurring_profiles">
              <Suspense fallback={<SectionLoader />}>
                <ViewRecurringProfilePage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path=":id/edit"
          element={
            <RequireAccess section="recurring_profiles">
              <Suspense fallback={<SectionLoader />}>
                <EditRecurringProfilePage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path=":id/duplicate"
          element={
            <RequireAccess section="recurring_profiles">
              <Suspense fallback={<SectionLoader />}>
                <DuplicateRecurringProfilePage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="add"
          element={
            <RequireAccess section="recurring_profiles">
              <Suspense fallback={<SectionLoader />}>
                <AddRecurringProfilePage />
              </Suspense>
            </RequireAccess>
          }
        />
      </Route>

      <Route
        path="search"
        element={<SearchPage />}
      />
      <Route
        path="*"
        element={
          <Suspense fallback={<SectionLoader />}>
            <NotFoundPage />
          </Suspense>
        }
      />
      <Route
        path="new-features"
        element={
          <RequireAccess section="new_features">
            <Suspense fallback={<SectionLoader />}>
              <NewFeaturesPage />
            </Suspense>
          </RequireAccess>
        }
      />
      <Route path="people">
        <Route
          index
          element={
            <RequireAccess section="people">
              <Suspense fallback={<SectionLoader />}>
                <PeoplePage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="invites"
          element={
            <RequireAccess section="people">
              <RequireAdditionalPermissions includePowerUser>
                <Suspense fallback={<SectionLoader />}>
                  <PeopleInvites />
                </Suspense>
              </RequireAdditionalPermissions>
            </RequireAccess>
          }
        />
        <Route
          path="archive"
          element={
            <RequireAccess section="people">
              <RequireAdditionalPermissions includePowerUser>
                <Suspense fallback={<SectionLoader />}>
                  <PeopleArchive />
                </Suspense>
              </RequireAdditionalPermissions>
            </RequireAccess>
          }
        />
      </Route>
      <Route path="companies">
        <Route
          index
          element={
            <RequireAccess section="people">
              <RequireAdditionalPermissions includePowerUser>
                <Suspense fallback={<SectionLoader />}>
                  <PeopleCompanies />
                </Suspense>
              </RequireAdditionalPermissions>
            </RequireAccess>
          }
        />
        <Route
          path=":companyId"
          element={
            <RequireExistingCompanyAccess>
              <RequireAccess section="people">
                <Suspense fallback={<SectionLoader />}>
                  <CompanyProfilePage />
                </Suspense>
              </RequireAccess>
            </RequireExistingCompanyAccess>
          }
        >
          <Route
            index
            element={
              <RequireExistingCompanyAccess>
                <RequireAccess section="people">
                  <Suspense fallback={<SectionLoader />}>
                    <CompanyProfileProjectsPage />
                  </Suspense>
                </RequireAccess>
              </RequireExistingCompanyAccess>
            }
          />
          <Route
            path="members"
            element={
              <RequireExistingCompanyAccess>
                <RequireAccess section="people">
                  <Suspense fallback={<SectionLoader />}>
                    <CompanyProfileMembersPage />
                  </Suspense>
                </RequireAccess>
              </RequireExistingCompanyAccess>
            }
          />
          <Route
            path="invoices"
            element={
              <RequireExistingCompanyAccess>
                <RequireAccess section="people">
                  <RequireAdditionalPermissions checkQuickBooksIntegrationInUse>
                    <Suspense fallback={<SectionLoader />}>
                      <CompanyProfileInvoicesPage />
                    </Suspense>
                  </RequireAdditionalPermissions>
                </RequireAccess>
              </RequireExistingCompanyAccess>
            }
          />
          <Route
            path="estimates"
            element={
              <RequireExistingCompanyAccess>
                <RequireAccess section="people">
                  <RequireAdditionalPermissions>
                    <Suspense fallback={<SectionLoader />}>
                      <CompanyProfileEstimatesPage />
                    </Suspense>
                  </RequireAdditionalPermissions>
                </RequireAccess>
              </RequireExistingCompanyAccess>
            }
          />
        </Route>
      </Route>
      <Route
        path="trash"
        element={
          <RequireAccess section="trash">
            <Suspense fallback={<SectionLoader />}>
              <TrashPage />
            </Suspense>
          </RequireAccess>
        }
      />
      <Route path="teams">
        <Route
          index
          element={
            <RequireAccess section="people">
              <RequireAdditionalPermissions includePowerUser>
                <Suspense fallback={<SectionLoader />}>
                  <PeopleTeams />
                </Suspense>
              </RequireAdditionalPermissions>
            </RequireAccess>
          }
        />
        <Route
          path=":teamId"
          element={
            <RequireAccess section="people">
              <RequireAdditionalPermissions includePowerUser>
                <Suspense fallback={<SectionLoader />}>
                  <TeamProfilePage />
                </Suspense>
              </RequireAdditionalPermissions>
            </RequireAccess>
          }
        >
          <Route
            index
            element={
              <RequireAccess section="people">
                <Suspense fallback={<SectionLoader />}>
                  <TeamMembersPage />
                </Suspense>
              </RequireAccess>
            }
          />
          <Route
            path="tasks"
            element={
              <RequireAccess section="people">
                <Suspense fallback={<SectionLoader />}>
                  <TeamTasksPage />
                </Suspense>
              </RequireAccess>
            }
          />
        </Route>
      </Route>
      <Route
        path="users/:userId"
        element={
          <RequireExistingUserAccess>
            <RequireAccess section="people">
              <Suspense fallback={<SectionLoader />}>
                <UserProfilePage />
              </Suspense>
            </RequireAccess>
          </RequireExistingUserAccess>
        }
      >
        <Route
          index
          element={
            <RequireActiveUserAccess>
              <RequireAccess section="people">
                <Suspense fallback={<SectionLoader />}>
                  <UserProfileProjectPage />
                </Suspense>
              </RequireAccess>
            </RequireActiveUserAccess>
          }
        />
        <Route
          path="tasks"
          element={
            <RequireActiveUserAccess>
              <RequireAccess section="people">
                <Suspense fallback={<SectionLoader />}>
                  <UserProfileTasksPage />
                </Suspense>
              </RequireAccess>
            </RequireActiveUserAccess>
          }
        />
        <Route
          path="timesheet"
          element={
            <RequireActiveUserAccess>
              <RequireCanSeeUserTimesheet>
                <RequireAccess section="people">
                  <Suspense fallback={<SectionLoader />}>
                    <UserProfileTimesheetPage />
                  </Suspense>
                </RequireAccess>
              </RequireCanSeeUserTimesheet>
            </RequireActiveUserAccess>
          }
        />
        <Route
          path="activity"
          element={
            <RequireAccess section="people">
              <Suspense fallback={<SectionLoader />}>
                <UserProfileActivityPage />
              </Suspense>
            </RequireAccess>
          }
        />
        <Route
          path="availability"
          element={
            <RequireFeature feature="availability">
              <RequireAccess section="people">
                <Suspense fallback={<SectionLoader />}>
                  <UserProfileAvailabilityPage />
                </Suspense>
              </RequireAccess>
            </RequireFeature>
          }
        />
      </Route>
      <Route
        path="reports"
        element={
          <RequireAccess section="reports">
            <Suspense fallback={<SectionLoader />}>
              <ReportsPage />
            </Suspense>
          </RequireAccess>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<SectionLoader />}>
              <AssignmentsPage />
            </Suspense>
          }
        />
        <Route
          path="all"
          element={
            <Suspense fallback={<SectionLoader />}>
              <AllReportsPage />
            </Suspense>
          }
        />
        <Route
          path="time/project"
          element={<Navigate to="/reports/all" />}
        />
        <Route
          path="time/dashboard"
          element={
            <Suspense fallback={<SectionLoader />}>
              <TimeDashboardPage />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="/reports/tasks"
        element={
          <Suspense fallback={<SectionLoader />}>
            <TasksFilterPage />
          </Suspense>
        }
      />
      <Route
        path="/reports/projects"
        element={
          <RequireAccess section="reports">
            <Suspense fallback={<SectionLoader />}>
              <ProjectsFilterPage />
            </Suspense>
          </RequireAccess>
        }
      />
      <Route
        path="/reports/time"
        element={
          <Suspense fallback={<SectionLoader />}>
            <TimeFilterPage />
          </Suspense>
        }
      />
      <Route
        path="/reports/uninvoiced"
        element={
          <Suspense fallback={<SectionLoader />}>
            <UninvoicedReportPage />
          </Suspense>
        }
      />
      <Route
        path="/reports/invoices"
        element={
          <Suspense fallback={<SectionLoader />}>
            <InvoicesFilterPage />
          </Suspense>
        }
      />
      <Route
        path="/reports/payments"
        element={
          <Suspense fallback={<SectionLoader />}>
            <PaymentsFilterPage />
          </Suspense>
        }
      />
    </Routes>
  );
};

export default MainRoutes;
